import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  Button,
  CircularProgress,
  Stack,
  Chip,
} from "@mui/material";
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  OpenInNew as OpenIcon,
  Description as FileIcon,
} from "@mui/icons-material";
import { tokens } from "@/tokens";
import { useLazyGetFirstSaleDocumentUrlQuery } from "@/services/first-sale";

interface DocumentViewerModalProps {
  document: any;
  open: boolean;
  onClose: () => void;
  pageNumber?: number;
  tenantId?: string;
}

const formatFileSize = (bytes: number | undefined) => {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
};

export const DocumentViewerModal: React.FC<DocumentViewerModalProps> = ({
  document: doc,
  open,
  onClose,
  pageNumber,
  tenantId,
}) => {
  const [fetchUrl, { data, isLoading }] = useLazyGetFirstSaleDocumentUrlQuery();

  React.useEffect(() => {
    if (open && doc?._id) {
      fetchUrl({ docId: doc._id, ...(tenantId && { tenantId }) });
    }
  }, [open, doc?._id, tenantId, fetchUrl]);

  const downloadUrl = data?.downloadUrl;
  const isImage = doc?.mimeType?.startsWith("image/");
  const isPdf = doc?.mimeType === "application/pdf";

  const viewerUrl = downloadUrl && isPdf && pageNumber
    ? `${downloadUrl}#page=${pageNumber}`
    : downloadUrl;

  const handleDownload = () => {
    if (!downloadUrl) return;
    const a = document.createElement("a");
    a.href = downloadUrl;
    a.download = doc?.filename || "document";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleOpenNewTab = () => {
    if (viewerUrl) window.open(viewerUrl, "_blank");
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: tokens.radius.lg,
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0, flex: 1 }}>
          <FileIcon sx={{ fontSize: 20, color: tokens.text.muted, flexShrink: 0 }} />
          <Typography
            sx={{
              fontSize: tokens.fontSize.base,
              fontWeight: tokens.fontWeight.heavy,
              color: tokens.text.primary,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {doc?.filename || "Document Viewer"}
          </Typography>
        </Stack>
        <IconButton onClick={onClose} size="small" sx={{ ml: 1 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Metadata row */}
      {doc && (
        <Box sx={{ px: 3, pb: 1.5 }}>
          <Stack direction="row" spacing={1}>
            {doc.mimeType && (
              <Chip
                label={doc.mimeType.split("/").pop()?.toUpperCase()}
                size="small"
                variant="outlined"
                sx={{ height: 22, fontSize: tokens.fontSize["2xs"] }}
              />
            )}
            {doc.sizeBytes && (
              <Chip
                label={formatFileSize(doc.sizeBytes)}
                size="small"
                variant="outlined"
                sx={{ height: 22, fontSize: tokens.fontSize["2xs"] }}
              />
            )}
            {pageNumber && (
              <Chip
                label={`Page ${pageNumber}`}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ height: 22, fontSize: tokens.fontSize["2xs"] }}
              />
            )}
          </Stack>
        </Box>
      )}

      <DialogContent sx={{ p: 0, height: "75vh" }}>
        {isLoading ? (
          <Stack alignItems="center" justifyContent="center" height="100%">
            <CircularProgress size={32} />
            <Typography sx={{ mt: 2, color: tokens.text.muted, fontSize: tokens.fontSize.sm2 }}>
              Loading document...
            </Typography>
          </Stack>
        ) : !downloadUrl ? (
          <Stack alignItems="center" justifyContent="center" height="100%">
            <FileIcon sx={{ fontSize: 48, color: tokens.text.disabled, mb: 1 }} />
            <Typography sx={{ color: tokens.text.secondary, fontSize: tokens.fontSize.sm2 }}>
              Document preview not available
            </Typography>
          </Stack>
        ) : isImage ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3, height: "100%", overflow: "auto", bgcolor: "#fafafa" }}>
            <img
              src={downloadUrl}
              alt={doc?.filename}
              style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", borderRadius: 8 }}
            />
          </Box>
        ) : (
          <iframe
            src={viewerUrl}
            title={doc?.filename}
            style={{ width: "100%", height: "100%", border: "none" }}
          />
        )}
      </DialogContent>

      {downloadUrl && (
        <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${tokens.border.light}` }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<OpenIcon sx={{ fontSize: 16 }} />}
            onClick={handleOpenNewTab}
            sx={{
              textTransform: "none",
              fontSize: tokens.fontSize.sm2,
              fontWeight: tokens.fontWeight.semibold,
              color: tokens.text.primary,
              borderColor: tokens.border.default,
              borderRadius: tokens.radius.md,
              "&:hover": { bgcolor: tokens.bg.hover, borderColor: tokens.text.muted },
            }}
          >
            Open in New Tab
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
            onClick={handleDownload}
            sx={{
              textTransform: "none",
              fontSize: tokens.fontSize.sm2,
              fontWeight: tokens.fontWeight.semibold,
              bgcolor: tokens.brand.primary,
              borderRadius: tokens.radius.md,
              boxShadow: "none",
              "&:hover": { bgcolor: tokens.brand.primaryHover, boxShadow: "none" },
            }}
          >
            Download
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};
